import { v4 as uuid } from 'uuid';
import { forkJoin, mergeMap, of } from "rxjs";
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatStates from '@medsurf/flat-states';
import * as FlatModels from "@medsurf/flat-models";
import * as FlatFacades from '../../internal';

@Injectable()
export class KeymapColumnEntityFacade {
  constructor(protected store: Store) {
  }

  //<editor-fold desc="Selectors">

  //</editor-fold>

  //<editor-fold desc="Requests">
  
  static getNewInstance(id: string,
                        annotation: FlatModels.AnnotationEntityModels.Keymap) {
    const keymapColumn: FlatModels.KeymapColumnEntityModels.KeymapColumn = {
      id,
      keymap: annotation.id,
      labels: [uuid(), uuid()],
      order: annotation.columns.findIndex(c => c === id),
    };
    return keymapColumn;
  }
  
  static addKeymapColumn(store: Store,
                         id: string,
                         page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                         image: FlatModels.ImageEntityModels.Image | null,
                         poi: FlatModels.PointOfInterestEntityModels.PointOfInterest | null,
                         annotation: FlatModels.AnnotationEntityModels.Keymap,
                         currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom) {
    // Get New Instance
    const keymapColumn = KeymapColumnEntityFacade.getNewInstance(id, annotation);
    
    // Create Keymap Column Pipe Object
    const keymapColumnPipeModel: FlatModels.PipeEntityModels.KeymapColumnPipeObject = {
      data: {},
      keymapColumn,
    }
    
    // Add Keymap Column
    return of(keymapColumnPipeModel).pipe(
      mergeMap((result: FlatModels.PipeEntityModels.KeymapColumnPipeObject) =>
        store.dispatch(new FlatActions.KeymapColumnEntityActions.AddEntity(keymapColumn)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        )
      ),
      mergeMap((result: FlatModels.PipeEntityModels.KeymapColumnPipeObject) => {
        if (keymapColumn.labels.length > 0) {
          return forkJoin(keymapColumn.labels.map((id, index) =>
            FlatFacades.KeymapLabelEntityFacade.addKeymapLabel(store, id, page, image, poi, keymapColumn, (index + 1).toString(), 'Legende ' + (index + 1), currentPositionAndZoom)
          )).pipe(
            mergeMap((keymapLabelPipeObjects: FlatModels.PipeEntityModels.KeymapLabelPipeObject[]) => {
              for(const keymapLabelPipeObject of keymapLabelPipeObjects) {
                if (result.labels) {
                  result.labels.push(keymapLabelPipeObject);
                } else {
                  result.labels = [keymapLabelPipeObject];
                }
              }
              return of(result);
            }),
          );
        }
        return of(result);
      }),
    );
  }
  
  static getCopyInstance(store: Store,
                         id: string,
                         annotation: FlatModels.AnnotationEntityModels.Keymap) {
    // Get Data
    const typedKeymapColumns = store.selectSnapshot(FlatStates.KeymapColumnEntityState.typedEntities$);
    
    // Get Keymap Column
    const keymapColumn = structuredClone(typedKeymapColumns[id]);
    
    // Update Keymap Column
    keymapColumn.id = uuid();
    keymapColumn.keymap = annotation.id;
    
    return keymapColumn;
  }
  
  static copyKeymapColumn(store: Store,
                          id: string,
                          annotation: FlatModels.AnnotationEntityModels.Keymap,
                          image: FlatModels.ImageEntityModels.Image | null,
                          clusterParentUpdate = false) {
    // Get Data
    const typedKeymapColumn = store.selectSnapshot(FlatStates.KeymapColumnEntityState.typedEntities$);
    
    // Get KeymapColumn 
    const keymapColumn = structuredClone(typedKeymapColumn[id]);
    
    // Update KeymapColumn 
    keymapColumn.id = uuid();
    
    // Update Annotation
    annotation.columns.push(keymapColumn.id);
    
    // Copy KeymapColumn
    return of({}).pipe(
      mergeMap(() => {
        const labels = structuredClone(keymapColumn.labels);
        keymapColumn.labels = []
        return FlatFacades.KeymapLabelEntityFacade.copyKeymapLabels(store, labels, keymapColumn, image, true)
      }),
      mergeMap(() =>
        store.dispatch(new FlatActions.KeymapColumnEntityActions.AddEntity(keymapColumn))
      ),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(annotation));
      }),
    );
  }
  
  static copyKeymapColumns(store: Store,
                           ids: string[],
                           annotation: FlatModels.AnnotationEntityModels.Keymap,
                           image: FlatModels.ImageEntityModels.Image | null,
                           clusterParentUpdate = false) {
    if (ids.length === 0) {
      return of([]);
    }
    
    // Copy Keymap Columns
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(ids.map(id => KeymapColumnEntityFacade.copyKeymapColumn(store, id, annotation, image,true)))
      ),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(annotation));
      }),
    )
  }
  
  static removeKeymapColumn(store: Store,
                            id: string,
                            clusterParentUpdate = false) {
    // Get Data
    const typedKeymapColumns = store.selectSnapshot(FlatStates.KeymapColumnEntityState.typedEntities$);
    const typedAnnotations = store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
    
    // Get Keymap Column
    const keymapColumn = structuredClone(typedKeymapColumns[id]);
    
    // Get Keymap
    const keymap = structuredClone(typedAnnotations[keymapColumn.keymap]) as FlatModels.AnnotationEntityModels.Keymap;
    keymap.columns.splice(keymap.columns.findIndex(id => id === keymapColumn.id), 1);
    
    // Remove Keymap Column
    return store.dispatch(new FlatActions.KeymapColumnEntityActions.RemoveEntity(keymapColumn)).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data, keymap});
        }
        return store.dispatch(new FlatActions.KeymapColumnEntityActions.RearrangeKeymapColumns(keymap.columns)).pipe(
          mergeMap(() =>
            store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(keymap))
          )
        );
      }),
    );
  }
  
  static removeKeymapColumns(store: Store,
                             ids: string[]) {
    if (ids.length === 0) {
      return of([]);
    }
    
    // Remove Groups
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(ids.map(id => KeymapColumnEntityFacade.removeKeymapColumn(store, id, true)))
      ),
      mergeMap((data: {data: Store, keymap: FlatModels.AnnotationEntityModels.Keymap}[]) => {
        const keymapMap = data.reduce((arr, clusterParentUpdate) => {
          const keymap = structuredClone(arr.get(clusterParentUpdate.keymap.id));
          if (keymap) {
            keymap.groups = clusterParentUpdate.keymap.columns.filter((id: string) => keymap.columns.includes(id));
            arr.set(clusterParentUpdate.keymap.id, keymap);
          } else {
            arr.set(clusterParentUpdate.keymap.id, clusterParentUpdate.keymap);
          }
          return arr;
        }, new Map<string, FlatModels.AnnotationEntityModels.Keymap>());
        const keymaps = Array.from(keymapMap.values());
        if (keymaps.length > 0) {
          return forkJoin(keymaps.map((keymap) =>
            store.dispatch(new FlatActions.KeymapColumnEntityActions.RearrangeKeymapColumns(keymap.columns)).pipe(
              mergeMap(() =>
                store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(keymap))
              )
            )
          ));
        }
        return of(data);
      }),
    );
  }
  
  //</editor-fold>
}
